import {
  plannerDashboardConfig,
  resolveFrontendEnvironmentPill,
  type FrontendEnvironmentPillConfig,
} from '@tov-plus/config';

export interface DashboardEnv {
  demoMode: boolean;
  apiBaseUrl: string;
  mcpServiceUrl: string;
  signInUrl: string;
  signUpUrl: string;
  signOutUrl: string | null;
  identityServiceUrl: string | null;
  docsUrl: string;
  devAuthHeaders: Record<string, string>;
  environmentPill: FrontendEnvironmentPillConfig | null;
}

export interface DashboardEnvSource {
  MODE?: string;
  VITE_DEMO?: string;
  VITE_DEMO_MODE?: string;
  VITE_API_BASE_URL?: string;
  VITE_MCP_SERVICE_URL?: string;
  VITE_IDENTITY_SERVICE_URL?: string;
  VITE_MARKETING_SITE_URL?: string;
  VITE_DOCS_SITE_URL?: string;
  VITE_DEV_AUTH_SUBJECT?: string;
  VITE_DEV_AUTH_EMAIL?: string;
  VITE_DEV_AUTH_SESSION_ID?: string;
  VITE_DEV_OCCASION_ID?: string;
  VITE_DEV_ACTOR_ID?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  PUBLIC_SENTRY_ENVIRONMENT?: string;
}

function resolveBrowserOrigin(browserOrigin: string | undefined): string {
  return browserOrigin?.trim().length
    ? browserOrigin.trim()
    : typeof window === 'undefined'
      ? 'http://127.0.0.1:4173'
      : window.location.origin;
}

function resolveMarketingSiteUrl(
  value: string | undefined,
  browserOrigin: string,
): string {
  const candidate =
    value && value.trim().length > 0
      ? value.trim()
      : (() => {
          const currentUrl = new URL(browserOrigin);
          const { hostname, protocol } = currentUrl;

          if (hostname === '127.0.0.1' || hostname === 'localhost') {
            return 'http://127.0.0.1:4321';
          }

          if (hostname.endsWith('.tovplus.local')) {
            return `${protocol}//landing.tovplus.local`;
          }

          if (
            hostname === 'dash.tovplus.com' ||
            hostname === 'admin.tovplus.com' ||
            hostname === 'vendors.tovplus.com'
          ) {
            return 'https://tovplus.com';
          }

          if (
            hostname.startsWith('release-stage-') &&
            hostname.endsWith('.tovplus.com')
          ) {
            return `${protocol}//release-stage-landing.tovplus.com`;
          }

          return plannerDashboardConfig.marketingUrl;
        })();

  try {
    return new URL(candidate).toString().replace(/\/$/, '');
  } catch {
    throw new Error(
      `VITE_MARKETING_SITE_URL must be a valid absolute URL. Received: ${candidate}`,
    );
  }
}

function resolveIdentityServiceUrl(
  value: string | undefined,
  browserOrigin: string,
): string | null {
  const candidate =
    value && value.trim().length > 0
      ? value.trim()
      : (() => {
          const currentUrl = new URL(browserOrigin);
          const { hostname, protocol } = currentUrl;

          if (hostname === '127.0.0.1' || hostname === 'localhost') {
            return 'http://127.0.0.1:3010';
          }

          if (
            hostname.startsWith('release-stage-') &&
            hostname.endsWith('.tovplus.com')
          ) {
            return `${protocol}//release-stage-identity.tovplus.com`;
          }

          if (hostname.endsWith('.tovplus.local')) {
            return `${protocol}//auth.tovplus.local`;
          }

          if (
            hostname === 'dash.tovplus.com' ||
            hostname === 'admin.tovplus.com' ||
            hostname === 'vendors.tovplus.com'
          ) {
            return 'https://identity.tovplus.com';
          }

          return null;
        })();

  if (!candidate) {
    return null;
  }

  try {
    return new URL(candidate).toString().replace(/\/$/, '');
  } catch {
    throw new Error(
      `VITE_IDENTITY_SERVICE_URL must be a valid absolute URL. Received: ${candidate}`,
    );
  }
}

function normalizeBaseUrl(value: string | undefined): string {
  const candidate =
    value && value.trim().length > 0 ? value.trim() : 'http://127.0.0.1:3001';

  try {
    return new URL(candidate).toString().replace(/\/$/, '');
  } catch {
    throw new Error(
      `VITE_API_BASE_URL must be a valid absolute URL. Received: ${candidate}`,
    );
  }
}

function resolveMcpServiceUrl(
  value: string | undefined,
  browserOrigin: string,
): string {
  const candidate =
    value && value.trim().length > 0
      ? value.trim()
      : (() => {
          const currentUrl = new URL(browserOrigin);
          const { hostname, protocol } = currentUrl;

          if (hostname === '127.0.0.1' || hostname === 'localhost') {
            return 'http://127.0.0.1:3021';
          }

          if (hostname.endsWith('.tovplus.local')) {
            return `${protocol}//mcp.tovplus.local`;
          }

          if (
            hostname.startsWith('release-stage-') &&
            hostname.endsWith('.tovplus.com')
          ) {
            return `${protocol}//release-stage-mcp.tovplus.com`;
          }

          if (
            hostname === 'dash.tovplus.com' ||
            hostname === 'admin.tovplus.com' ||
            hostname === 'vendors.tovplus.com'
          ) {
            return 'https://mcp.tovplus.com';
          }

          return 'http://127.0.0.1:3021';
        })();

  try {
    return new URL(candidate).toString().replace(/\/$/, '');
  } catch {
    throw new Error(
      `VITE_MCP_SERVICE_URL must be a valid absolute URL. Received: ${candidate}`,
    );
  }
}

function normalizeAuthUrl(input: {
  path: string;
  marketingSiteUrl: string;
  browserOrigin: string;
  identityServiceUrl: string | null;
}): string {
  if (
    input.identityServiceUrl !== null &&
    (input.path === '/signin' || input.path === '/signup')
  ) {
    const authUrl = new URL(
      input.path === '/signup' ? '/auth/register' : '/auth/login',
      input.identityServiceUrl,
    );
    authUrl.searchParams.set('return_to', input.browserOrigin);
    return authUrl.toString();
  }

  const defaultUrl = new URL(input.path, input.marketingSiteUrl);
  defaultUrl.searchParams.set('return_to', input.browserOrigin);

  return defaultUrl.toString();
}

function normalizeLogoutUrl(
  identityServiceUrl: string | null,
  browserOrigin: string,
): string | null {
  if (identityServiceUrl === null) {
    return null;
  }

  const logoutUrl = new URL('/auth/logout', identityServiceUrl);
  logoutUrl.searchParams.set(
    'return_to',
    new URL('/signed-out', browserOrigin).toString(),
  );

  return logoutUrl.toString();
}

function resolveDocsSiteUrl(
  value: string | undefined,
  browserOrigin: string,
): string {
  const candidate =
    value && value.trim().length > 0
      ? value.trim()
      : (() => {
          const currentUrl = new URL(browserOrigin);
          const { hostname, protocol } = currentUrl;

          if (hostname === '127.0.0.1' || hostname === 'localhost') {
            return 'http://127.0.0.1:4322';
          }

          if (hostname.endsWith('.tovplus.local')) {
            return `${protocol}//docs.tovplus.local`;
          }

          if (
            hostname.startsWith('release-stage-') &&
            hostname.endsWith('.tovplus.com')
          ) {
            return `${protocol}//release-stage-docs.tovplus.com`;
          }

          return plannerDashboardConfig.docsUrl;
        })();

  try {
    return new URL(candidate).toString().replace(/\/$/, '');
  } catch {
    throw new Error(
      `VITE_DOCS_SITE_URL must be a valid absolute URL. Received: ${candidate}`,
    );
  }
}

function isProductionDashboardOrigin(browserOrigin: string) {
  try {
    return new URL(browserOrigin).hostname === 'dash.tovplus.com';
  } catch {
    return false;
  }
}

function appendHeader(
  headers: Record<string, string>,
  key: string,
  value: string | undefined,
) {
  if (value && value.trim().length > 0) {
    headers[key] = value.trim();
  }
}

function parseDemoMode(source: DashboardEnvSource, browserOrigin: string) {
  const demoMode =
    source.VITE_DEMO === 'true' || source.VITE_DEMO_MODE === 'true';

  if (demoMode && isProductionDashboardOrigin(browserOrigin)) {
    throw new Error('Demo mode cannot be enabled on dash.tovplus.com.');
  }

  return demoMode;
}

export function parseDashboardEnv(
  source: DashboardEnvSource,
  browserOrigin?: string,
): DashboardEnv {
  const devAuthHeaders: Record<string, string> = {};
  const resolvedBrowserOrigin = resolveBrowserOrigin(browserOrigin);
  const demoMode = parseDemoMode(source, resolvedBrowserOrigin);
  const allowDevAuthHeaders = !isProductionDashboardOrigin(
    resolvedBrowserOrigin,
  );
  const identityServiceUrl = resolveIdentityServiceUrl(
    source.VITE_IDENTITY_SERVICE_URL,
    resolvedBrowserOrigin,
  );
  const marketingSiteUrl = resolveMarketingSiteUrl(
    source.VITE_MARKETING_SITE_URL,
    resolvedBrowserOrigin,
  );
  const docsUrl = resolveDocsSiteUrl(
    source.VITE_DOCS_SITE_URL,
    resolvedBrowserOrigin,
  );
  const apiBaseUrl = normalizeBaseUrl(source.VITE_API_BASE_URL);
  const mcpServiceUrl = resolveMcpServiceUrl(
    source.VITE_MCP_SERVICE_URL,
    resolvedBrowserOrigin,
  );

  if (allowDevAuthHeaders) {
    appendHeader(
      devAuthHeaders,
      'x-keycloak-sub',
      source.VITE_DEV_AUTH_SUBJECT,
    );
    appendHeader(
      devAuthHeaders,
      'x-keycloak-email',
      source.VITE_DEV_AUTH_EMAIL,
    );
    appendHeader(
      devAuthHeaders,
      'x-keycloak-session-id',
      source.VITE_DEV_AUTH_SESSION_ID,
    );
    appendHeader(
      devAuthHeaders,
      'x-tov-occasion-id',
      source.VITE_DEV_OCCASION_ID,
    );
    appendHeader(devAuthHeaders, 'x-tov-actor-id', source.VITE_DEV_ACTOR_ID);
  }

  return {
    demoMode,
    apiBaseUrl,
    mcpServiceUrl,
    signInUrl: normalizeAuthUrl({
      path: plannerDashboardConfig.plannerSignInUrl,
      marketingSiteUrl,
      browserOrigin: resolvedBrowserOrigin,
      identityServiceUrl,
    }),
    signUpUrl: normalizeAuthUrl({
      path: plannerDashboardConfig.plannerSignUpUrl,
      marketingSiteUrl,
      browserOrigin: resolvedBrowserOrigin,
      identityServiceUrl,
    }),
    signOutUrl: normalizeLogoutUrl(identityServiceUrl, resolvedBrowserOrigin),
    identityServiceUrl,
    docsUrl,
    devAuthHeaders,
    environmentPill: resolveFrontendEnvironmentPill({
      apiBaseUrl,
      environmentName:
        source.VITE_SENTRY_ENVIRONMENT ?? source.PUBLIC_SENTRY_ENVIRONMENT,
      identityServiceUrl,
      siteUrl: resolvedBrowserOrigin,
    }),
  };
}

export const dashboardEnv = parseDashboardEnv(
  import.meta.env as unknown as DashboardEnvSource,
);
